import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { motion } from 'framer-motion';
import { useAuth } from './auth/AuthContext';
import api from '../utils/api';

const PublishAmplifiedEvent = () => {
  const navigate = useNavigate();
  const { isAdminAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    eventName: '',
    dateTime: null,
    venue: '',
    organizer: '',
    objective: '',
    targetAudience: '',
    eventTheme: '',
    schedule: '',
    keySpeakers: '',
    workshops: '',
    entryRequirements: '',
    parkingTransport: '',
    cateringRefreshments: '',
    dressCode: '',
    sponsorships: '',
    contactInformation: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.eventName.trim()) newErrors.eventName = 'Event name is required';
    if (!formData.dateTime) newErrors.dateTime = 'Date and time are required';
    if (!formData.venue.trim()) newErrors.venue = 'Venue is required';
    if (!formData.organizer.trim()) newErrors.organizer = 'Organizer is required';
    if (!formData.contactInformation.trim()) newErrors.contactInformation = 'Contact information is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isAdminAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login as admin first',
        severity: 'error',
      });
      return;
    }

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    try {
      await api.post('/api/schools/publish-amplified-event', {
        ...formData,
        dateTime: formData.dateTime ? formData.dateTime.toISOString() : null,
      });
      setSnackbar({
        open: true,
        message: 'National event published successfully!',
        severity: 'success',
      });
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      console.error('Error publishing event:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to publish event. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatLabel = (key) =>
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="sm" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 4, textAlign: 'center' }}>
            Publish National Event
          </Typography>
          <Paper sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {Object.entries(formData).map(([key, value], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500, mb: 1 }}>
                    {formatLabel(key)}
                  </Typography>
                  {key === 'dateTime' ? (
                    <DateTimePicker
                      value={value}
                      onChange={(newValue) => handleChange('dateTime', newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.dateTime}
                          helperText={errors.dateTime}
                          sx={{ bgcolor: '#fff' }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      multiline={key === 'objective' || key === 'schedule'}
                      rows={key === 'objective' || key === 'schedule' ? 4 : 1}
                      value={value}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                      error={!!errors[key]}
                      helperText={errors[key]}
                      sx={{ bgcolor: '#fff' }}
                    />
                  )}
                </Box>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Object.keys(formData).length * 0.05 }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth
                sx={{
                  bgcolor: '#6200ee',
                  '&:hover': { bgcolor: '#7f39fb' },
                  borderRadius: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  mt: 2,
                }}
              >
                Publish Event
              </Button>
            </motion.div>
          </Paper>
        </motion.div>
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default PublishAmplifiedEvent;